package com.qrkontaq.ui.admin

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div

internal data class AreaSeries(
    val label: String,
    val color: String,
    val points: List<Double>,
)

/**
 * Smoothed multi-series area chart rendered as inline SVG. Smoothing uses a
 * Catmull-Rom spline converted to cubic Beziers; a transparent hit-strip per
 * x-index drives a floating tooltip.
 */
@Composable
internal fun QpAreaChart(
    labels: List<String>,
    series: List<AreaSeries>,
    height: Int = 220,
) {
    if (labels.isEmpty() || series.all { it.points.all { p -> p <= 0.0 } }) {
        SpanText(PlatformT.noData, Modifier.fontSize(13.px).color(Color.rgba(242, 247, 244, 0.55f)))
        return
    }
    var hover by remember { mutableStateOf(-1) }
    val n = labels.size
    val w = 1000.0
    val h = height.toDouble()
    val padY = 14.0
    val maxV = series.flatMap { it.points }.maxOrNull()?.coerceAtLeast(1.0) ?: 1.0

    fun x(i: Int) = if (n <= 1) 0.0 else w * i / (n - 1)
    fun y(v: Double) = h - padY - (h - 2 * padY) * (v / maxV)

    val svg = buildString {
        append("<svg viewBox=\"0 0 $w $h\" preserveAspectRatio=\"none\">")
        append("<defs>")
        series.forEachIndexed { si, s ->
            append("<linearGradient id=\"qpg$si\" x1=\"0\" y1=\"0\" x2=\"0\" y2=\"1\">")
            append("<stop offset=\"0\" stop-color=\"${s.color}\" stop-opacity=\"0.32\"/>")
            append("<stop offset=\"1\" stop-color=\"${s.color}\" stop-opacity=\"0\"/>")
            append("</linearGradient>")
        }
        append("</defs>")
        series.forEachIndexed { si, s ->
            val pts = s.points.mapIndexed { i, v -> x(i) to y(v) }
            val line = smoothPath(pts)
            append("<path d=\"$line L ${x(n - 1)} $h L ${x(0)} $h Z\" fill=\"url(#qpg$si)\"/>")
            append("<path d=\"$line\" fill=\"none\" stroke=\"${s.color}\" stroke-width=\"2.4\" stroke-linecap=\"round\" stroke-linejoin=\"round\"/>")
        }
        if (hover in 0 until n) {
            val hx = x(hover)
            append("<line x1=\"$hx\" y1=\"0\" x2=\"$hx\" y2=\"$h\" stroke=\"rgba(255,255,255,.18)\" stroke-width=\"1\"/>")
            series.forEach { s ->
                val v = s.points.getOrElse(hover) { 0.0 }
                append("<circle cx=\"$hx\" cy=\"${y(v)}\" r=\"3.6\" fill=\"${s.color}\" stroke=\"#050E0A\" stroke-width=\"1.5\"/>")
            }
        }
        append("</svg>")
    }

    Box(Modifier.fillMaxWidth().position(org.jetbrains.compose.web.css.Position.Relative)) {
        Box(
            Modifier.fillMaxWidth().attrsModifier { classes("qp-chart") },
        ) {
            Div(attrs = {
                classes("qp-chart")
                ref { el ->
                    el.innerHTML = svg
                    onDispose { el.innerHTML = "" }
                }
            })
        }
        // Hit strips over each x index.
        Row(Modifier.fillMaxWidth().height(height.px).position(org.jetbrains.compose.web.css.Position.Absolute).top(0.px)) {
            (0 until n).forEach { i ->
                Box(
                    Modifier.styleModifier { property("flex", "1") }.fillMaxHeight()
                        .attrsModifier {
                            classes("qp-hit")
                            onMouseEnter { hover = i }
                            onMouseLeave { hover = -1 }
                        },
                )
            }
        }
        if (hover in 0 until n) {
            val leftPct = if (n <= 1) 50.0 else 100.0 * hover / (n - 1)
            Box(
                Modifier
                    .position(org.jetbrains.compose.web.css.Position.Absolute)
                    .attrsModifier { classes("qp-tip-float") }
                    .styleModifier { property("left", "$leftPct%"); property("top", "0"); property("opacity", "1") },
            ) {
                Column {
                    SpanText(
                        labels.getOrElse(hover) { "" },
                        Modifier.fontSize(11.px).fontWeight(FontWeight.Bold).color(Color.rgb(0xF2, 0xF7, 0xF4)),
                    )
                    series.forEach { s ->
                        Row(Modifier.gap(6.px), verticalAlignment = Alignment.CenterVertically) {
                            Box(Modifier.size(8.px).borderRadius(2.px).styleModifier { property("background", s.color) })
                            SpanText(
                                "${s.label}: ${s.points.getOrElse(hover) { 0.0 }.toLong()}",
                                Modifier.fontSize(11.px).color(Color.rgba(242, 247, 244, 0.85f)),
                            )
                        }
                    }
                }
            }
        }
    }
}

/** Catmull-Rom -> cubic Bezier path string through the given points. */
private fun smoothPath(pts: List<Pair<Double, Double>>): String {
    if (pts.isEmpty()) return ""
    if (pts.size == 1) return "M ${pts[0].first} ${pts[0].second}"
    val sb = StringBuilder("M ${pts[0].first} ${pts[0].second}")
    for (i in 0 until pts.size - 1) {
        val p0 = pts[if (i - 1 < 0) 0 else i - 1]
        val p1 = pts[i]
        val p2 = pts[i + 1]
        val p3 = pts[if (i + 2 >= pts.size) pts.size - 1 else i + 2]
        val c1x = p1.first + (p2.first - p0.first) / 6.0
        val c1y = p1.second + (p2.second - p0.second) / 6.0
        val c2x = p2.first - (p3.first - p1.first) / 6.0
        val c2y = p2.second - (p3.second - p1.second) / 6.0
        sb.append(" C $c1x $c1y $c2x $c2y ${p2.first} ${p2.second}")
    }
    return sb.toString()
}

/** Donut chart with a centre total and a wrapped legend. */
@Composable
internal fun QpDonut(
    items: List<Pair<String, Long>>,
    colors: List<String>,
    size: Int = 148,
) {
    val total = items.sumOf { it.second }
    if (total <= 0) {
        SpanText(PlatformT.noData, Modifier.fontSize(13.px).color(Color.rgba(242, 247, 244, 0.55f)))
        return
    }
    val r = 45.0
    val c = 2 * kotlin.math.PI * r
    var acc = 0.0
    val svg = buildString {
        append("<svg viewBox=\"0 0 120 120\">")
        append("<circle cx=\"60\" cy=\"60\" r=\"$r\" fill=\"none\" stroke=\"rgba(255,255,255,.06)\" stroke-width=\"14\"/>")
        items.forEachIndexed { i, (_, v) ->
            val frac = v.toDouble() / total
            val len = frac * c
            val color = colors[i % colors.size]
            append(
                "<circle cx=\"60\" cy=\"60\" r=\"$r\" fill=\"none\" stroke=\"$color\" stroke-width=\"14\" " +
                    "stroke-dasharray=\"$len ${c - len}\" stroke-dashoffset=\"${-acc}\" " +
                    "transform=\"rotate(-90 60 60)\" stroke-linecap=\"butt\"/>",
            )
            acc += len
        }
        append("</svg>")
    }
    Row(Modifier.fillMaxWidth().gap(16.px), verticalAlignment = Alignment.CenterVertically) {
        Box(Modifier.size(size.px).position(org.jetbrains.compose.web.css.Position.Relative)) {
            Div(attrs = {
                ref { el ->
                    el.innerHTML = svg
                    onDispose { el.innerHTML = "" }
                }
            })
            Box(
                Modifier.fillMaxSize().position(org.jetbrains.compose.web.css.Position.Absolute).top(0.px).left(0.px),
                contentAlignment = Alignment.Center,
            ) {
                SpanText(
                    "$total",
                    Modifier.fontSize(20.px).fontWeight(FontWeight.Bold).color(Color.rgb(0xF2, 0xF7, 0xF4))
                        .attrsModifier { classes("qp-display") },
                )
            }
        }
        Column(Modifier.gap(7.px).styleModifier { property("flex", "1"); property("min-width", "0") }) {
            items.forEachIndexed { i, (name, v) ->
                Row(
                    Modifier.fillMaxWidth().gap(8.px),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.SpaceBetween,
                ) {
                    Row(Modifier.gap(7.px), verticalAlignment = Alignment.CenterVertically) {
                        Box(
                            Modifier.size(9.px).borderRadius(3.px)
                                .styleModifier { property("background", colors[i % colors.size]) },
                        )
                        SpanText(name, Modifier.fontSize(12.px).color(Color.rgba(242, 247, 244, 0.82f)))
                    }
                    SpanText(
                        "$v",
                        Modifier.fontSize(12.px).fontWeight(FontWeight.Bold).color(Color.rgb(0xF2, 0xF7, 0xF4)),
                    )
                }
            }
        }
    }
}

/** Horizontal funnel: each stage's width scales to the first; conversion % between stages. */
@Composable
internal fun QpFunnel(stages: List<Pair<String, Long>>) {
    if (stages.isEmpty()) return
    val top = stages.first().second.coerceAtLeast(1)
    Column(Modifier.fillMaxWidth().gap(10.px)) {
        stages.forEachIndexed { i, (label, value) ->
            val pct = (value * 100 / top).coerceIn(0, 100)
            val fromPrev =
                if (i == 0) null else {
                    val prev = stages[i - 1].second
                    if (prev <= 0) 0 else (value * 100 / prev)
                }
            Column(Modifier.fillMaxWidth().gap(4.px)) {
                Row(
                    Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    SpanText(label, Modifier.fontSize(12.px).color(Color.rgba(242, 247, 244, 0.72f)))
                    Row(Modifier.gap(8.px), verticalAlignment = Alignment.CenterVertically) {
                        if (fromPrev != null) {
                            SpanText(
                                "$fromPrev%",
                                Modifier.fontSize(11.px).color(Color.rgb(0x7E, 0xE2, 0xBC)),
                            )
                        }
                        SpanText(
                            "$value",
                            Modifier.fontSize(13.px).fontWeight(FontWeight.Bold).color(Color.rgb(0xF2, 0xF7, 0xF4))
                                .attrsModifier { classes("qp-display") },
                        )
                    }
                }
                Box(
                    Modifier.attrsModifier { classes("qp-funnel-bar") }
                        .styleModifier { property("width", "${pct.coerceAtLeast(6)}%") },
                ) {}
            }
        }
    }
}
